import styled, { css } from 'styled-components';
import colors from 'styles/colors';
import borders from 'styles/borders';
import sizes from 'styles/sizes';
import { KeyboardShortcutTooltipText, TooltipContainer, TooltipText } from './Common';

export enum AddStepButtonVariant {
  /** Compact step bar pill */
  Chip = 'chip',
  /** Full-width form sidebar button */
  Block = 'block',
}

const ADD_STEP_BG = '#2479F2';
const ADD_STEP_HOVER_BG = '#244B99';
const ADD_STEP_DISABLED_BG = colors.gray5;

const chipStyles = css`
  border-radius: 100px;
  padding: 6px;
  font-size: ${sizes.textBody2};
  font-family: inherit;
  display: inline-flex;
  align-self: stretch;
  flex-shrink: 0;
  white-space: nowrap;
`;

const blockStyles = css`
  width: 100%;
  padding: 0 12px;
  min-height: 50px;
  border-radius: ${borders.borderRadius.xlarge};
`;

const StyledButton = styled.button<{
  $variant: AddStepButtonVariant;
  $forceShowTooltip?: boolean;
}>`
  background-color: ${ADD_STEP_BG};
  color: ${colors.gray10};
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  transition: background-color 0.15s ease;
  user-select: none;

  &:hover {
    background-color: ${ADD_STEP_HOVER_BG};
  }

  &:disabled {
    background-color: ${ADD_STEP_DISABLED_BG};
    color: rgba(236, 236, 236, 0.64);
    cursor: default;

    span {
      color: rgba(236, 236, 236, 0.64);
    }

    &:hover {
      background-color: ${ADD_STEP_DISABLED_BG};
    }
  }

  &:hover .tooltip,
  ${(props) => props.$forceShowTooltip && '.tooltip'} {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  ${(props) => (props.$variant === AddStepButtonVariant.Chip ? chipStyles : blockStyles)}
`;

const Tooltip = styled(TooltipContainer)`
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 8px;
  padding: 6px 10px;

  &::after {
    left: 50%;
    transform: translateX(-50%);
  }
`;

const BlockLabel = styled.span`
  font-size: ${sizes.textBody1};
  font-weight: 500;
  color: ${colors.gray10};
  line-height: normal;
  padding: 0 2px;
`;

export interface AddStepButtonProps {
  /** Chip for the compact step bar pill, Block for the full-width form sidebar button */
  variant: AddStepButtonVariant;
  onClick?: () => void;
  /** Tooltip text shown on hover (or when disabled, for chip variant with disabledText) */
  hoverText?: string;
  /** Text shown when disabled (chip variant only; block variant uses hoverText) */
  disabledText?: string;
  /** Keyboard shortcut to display in tooltip */
  keyboardShortcut?: string;
  disabled?: boolean;
  /** Force show the tooltip (useful for Storybook) */
  forceShowTooltip?: boolean;
}

// Semantic anchor used by StepsConfigSection to scroll the add-step button into view, decoupling
// that lookup from test ids.
export const ADD_STEP_BUTTON_ATTR = 'data-add-step-button';

export const AddStepButton = ({
  variant,
  onClick,
  hoverText,
  disabledText,
  keyboardShortcut,
  disabled,
  forceShowTooltip,
}: AddStepButtonProps) => {
  const tooltipText = disabled && disabledText ? disabledText : hoverText;
  const showTooltip = tooltipText || (!disabled && keyboardShortcut);

  return (
    <StyledButton
      type="button"
      data-testid={variant === AddStepButtonVariant.Chip ? 'step-add-chip' : 'add-step-button'}
      {...{ [ADD_STEP_BUTTON_ATTR]: '' }}
      disabled={disabled}
      $variant={variant}
      $forceShowTooltip={forceShowTooltip}
      onClick={onClick}
    >
      {showTooltip && (
        <Tooltip className="tooltip">
          {tooltipText && <TooltipText>{tooltipText}</TooltipText>}
          {!disabled && keyboardShortcut && (
            <KeyboardShortcutTooltipText>{keyboardShortcut}</KeyboardShortcutTooltipText>
          )}
        </Tooltip>
      )}
      {variant === AddStepButtonVariant.Chip ? (
        <span style={{ padding: '0 2px', lineHeight: '16px' }}>Add Step</span>
      ) : (
        <BlockLabel>Add Step</BlockLabel>
      )}
    </StyledButton>
  );
};
